import { Download } from '@mui/icons-material';
import { Box, Button, Card, Typography } from '@mui/material';
import { CheckboxField } from 'components/CheckboxField';
import { Selector } from 'components/Selector';
import { useConfigContext } from 'hooks';
import { useCallback, useState } from 'react';
import { StatsTimelineActioner } from 'services';
import { ExportActioner } from 'services/export/actioner';
import './GeneralStats.css';

const LineSection = ({ data }) => {
  if (typeof data !== 'object' || Array.isArray(data)) {
    return <></>;
  }
  return (
    <>
      {Object.keys(data).map(line => {
        if (Array.isArray(data[line])) {
          const header = data[line][0] ?? [];
          const body = data[line].filter((_, index) => index > 0);
          if (header.length === 0) {
            return (
              <Typography key={line}>
                {line} : <strong>Aucune données</strong>
              </Typography>
            );
          }
          return (
            <>
              <Typography style={{ marginTop: '10px' }} key={line}>
                <strong>{line} :</strong>
              </Typography>
              <div className="stats-table-wrapper">
                <table className="stats-table">
                  <tr>
                    {header.map(r => {
                      return (
                        <th className="padding-all" colSpan={r.colspan ?? 1} rowSpan={r.rowspan ?? 1}>
                          {r.title}
                        </th>
                      );
                    })}
                  </tr>
                  {body.map(line => {
                    return (
                      <tr>
                        {line.map(r => {
                          return <td className="padding-all">{r}</td>;
                        })}
                      </tr>
                    );
                  })}
                </table>
              </div>
            </>
          );
        }
        if (typeof data[line] === 'object') {
          return (
            <>
              <Typography key={line}>
                <strong>{line} :</strong>
              </Typography>
              <div className="padding-left">
                <LineSection data={data[line]} />
              </div>
            </>
          );
        }
        return (
          <Typography key={line}>
            {line} : <strong>{data[line]?.toString()}</strong>
          </Typography>
        );
      })}
    </>
  );
};

const DisplaySection = ({ data, title, dataType, annee, onlyYear }) => {
  const { campaign, site } = useConfigContext();
  const onExport = () =>
    ExportActioner.download('stats', { campaign, site, dataType, subType: title, annee, onlyYear });
  return (
    <>
      <Card className="stats-general-card">
        <Typography className="stats-general-title" variant="h2" fontWeight="bold">
          {' '}
          {title}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <section style={{ marginLeft: '40px', minWidth: 0, flex: 1 }}>
            <Typography className="stats-general-title" variant="h3" fontWeight="bold">
              Statistique
            </Typography>
            <LineSection data={data.result} />
          </section>
          <section style={{ marginRight: '40px' }}>
            {data?.target !== undefined && (
              <>
                <Box className="stats-general-title" sx={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Typography variant="h3">Cibles</Typography>
                  <Button
                    disabled={data?.target?.length === 0}
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<Download />}
                    onClick={onExport}
                  >
                    Export
                  </Button>
                </Box>
                <Box className="stats-general-target">
                  {data?.target.map((st, i) => {
                    if (typeof st === 'object') {
                      return (
                        <Typography key={i}>
                          {' '}
                          {st?.general_information?.last_name} {st?.general_information?.first_name}{' '}
                        </Typography>
                      );
                    }
                    return <Typography key={st}>{st}</Typography>;
                  })}
                </Box>
              </>
            )}
          </section>
        </Box>
      </Card>
    </>
  );
};

const StatsGeneral = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState('asl');
  const today = new Date().getFullYear();
  const [annee, setAnnee] = useState(today);
  const [targetYear, setTargetYear] = useState(false);
  const [loadedParams, setLoadedParams] = useState({ dataType: 'asl', annee: today, onlyYear: false });
  const { campaign, site } = useConfigContext();
  const allYear = [
    { label: today - 3, value: today - 3 },
    { label: today - 2, value: today - 2 },
    { label: today - 1, value: today - 1 },
    { label: today, value: today },
    { label: today + 1, value: today + 1 },
  ];
  const search = useCallback(async () => {
    setLoading(true);
    const receviedData = await StatsTimelineActioner.getGeneral({
      dataType: target,
      annee: annee,
      campagne: campaign,
      site: site,
      onlyYear: targetYear,
    });
    setData(receviedData);
    setLoadedParams({ dataType: target, annee, onlyYear: targetYear });
    setLoading(false);
  }, [target, campaign, annee, targetYear, site]);

  const handleSubmit = () => {
    search();
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <Selector
          className="stats-general-selector"
          label="Choix"
          labelId="target"
          selected={target}
          setSelected={setTarget}
          items={[
            { label: 'Ville Senior', value: 'villeLilleSenior' },
            { label: 'Ville Handicap Adultes', value: 'villeLilleHardiAdultes' },
            { label: 'Ville Handicap Enfants', value: 'villeLilleHardiJeunes' },
            { label: 'Ville Jeunesse', value: 'villeLilleJeunesse' },
            { label: 'MSB', value: 'msb' },
            { label: 'Médiation', value: 'mediation' },
            { label: 'Atelier - ASL', value: 'asl' },
            { label: 'Atelier - Parents', value: 'parent' },
            { label: 'Atelier - CLAS', value: 'clas' },
            { label: 'CLAS - Individuel', value: 'clasIndividuel' },
            { label: 'DELF', value: 'delf' },
            { label: 'Insertion', value: 'insertion' },
            { label: 'MEL/CV', value: 'MELCV' },
            { label: 'Un jeune, Un mentor', value: 'unJeuneUnMentor' },
            { label: 'Durée Inscription', value: 'dureeInscription' },
            { label: 'Stagiaires', value: 'stagiaires' },
            { label: 'Bénévoles', value: 'benevoles' },
            { label: 'Apprenants', value: 'apprenants' },
            { label: 'Matching Stats', value: 'matchingStats' },
          ]}
        />
        {target !== 'matchingStats' && (
          <Selector
            className="stats-general-selector"
            label="Annee"
            selected={annee}
            setSelected={setAnnee}
            items={allYear}
          />
        )}
        <Button className="stats-general-button" variant="contained" onClick={handleSubmit}>
          Load
        </Button>
        {target !== 'matchingStats' && (
          <CheckboxField label="Utiliser uniquement l'année" checked={targetYear} setField={setTargetYear} />
        )}
      </Box>
      {loading ? (
        'loading...'
      ) : data?.result ? (
        <DisplaySection data={data} {...loadedParams} />
      ) : (
        Object.keys(data).map(file => {
          return <DisplaySection key={file} title={file} data={data[file]} {...loadedParams} />;
        })
      )}
    </>
  );
};

export { StatsGeneral };
